import { BackgroundStyleType, HeaderPositionType, PositionType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

const DIRECT_WORDCLOUD_PATTERN = /\b(word\s*cloud|wordcloud|tag\s*cloud|wordle|keyword\s*cloud|theme\s*cloud|buzzword\s*cloud)\b/i;

export class WordCloudCategory extends BaseCategory {
    get name() {
        return "Word Cloud";
    }

    get type() {
        return SlideCategories.WORD_CLOUD;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use when the user asks for a word cloud, tag cloud, wordle, keyword cloud, or a visual cluster of weighted terms such as survey themes, feedback themes, or sentiment words.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a word cloud slide.
        Return a ranked list of 10-50 unique words or short phrases in descending order of importance.
        Prefer short, high-signal keywords that visually represent themes, feedback, sentiment, or repeated concepts.
        If the user explicitly asks for a word cloud, tag cloud, or wordle, optimize for that visual treatment first.`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema() {
        return {
            words: ["ranked array of 10-50 unique words or short phrases in descending order of importance"],
            summaryLine: "optional short explanation of what the cloud represents",
        };
    }

    validateGeneratedModel(model) {
        return this.normalizeWords(model).length > 0;
    }

    normalizeWords(model = {}) {
        const rawWordArrays = [
            model.words,
            model.keywords,
            model.terms,
            model.textResults,
        ];

        for (const rawValue of rawWordArrays) {
            if (Array.isArray(rawValue)) {
                const normalized = rawValue
                    .map(value => typeof value === "string" ? value.trim() : `${value?.word || value?.text || value?.title || ""}`.trim())
                    .filter(Boolean);

                if (normalized.length > 0) {
                    return [...new Set(normalized)].slice(0, 50);
                }
            }
        }

        const textCandidates = [
            model.slideTitle,
            model.userPrompt,
            model.prompt,
            model.summaryLine,
        ].filter(value => typeof value === "string" && value.trim().length > 0);

        for (const text of textCandidates) {
            if (!DIRECT_WORDCLOUD_PATTERN.test(text)) {
                continue;
            }

            const stripped = text
                .replace(DIRECT_WORDCLOUD_PATTERN, " ")
                .replace(/\b(for|about|on|of|around)\b/gi, " ")
                .replace(/[^\w\s-]/g, " ")
                .replace(/\s+/g, " ")
                .trim();

            const words = stripped
                .split(" ")
                .map(word => word.trim())
                .filter(word => word.length > 2);

            if (words.length > 0) {
                return [...new Set(words)].slice(0, 20);
            }
        }

        return [];
    }

    getSlideModels(model) {
        const normalized = this.normalizeModel(model);

        return [
            this.buildWordCloudModel(normalized),
        ];
    }

    normalizeModel(model = {}) {
        const words = this.normalizeWords(model);
        const summaryLine = `${model.summaryLine || model.slideSubTitle || model.conclusion || ""}`.trim();
        const slideTitle = `${model.slideTitle || "Word Cloud"}`.trim() || "Word Cloud";

        return {
            ...model,
            slideTitle,
            summaryLine,
            words,
        };
    }

    buildWordCloudModel(model) {
        const builder = new SlideModelBuilder("wordCloud", model, true);
        builder.model.layout.backgroundStyle = BackgroundStyleType.ACCENT;
        builder.model.layout.backgroundColor = "background_accent";
        builder.model.layout.headerPosition = HeaderPositionType.NONE;

        builder.createPrimary({
            style: "right_angles",
            sort: "size",
            scale: 25,
            calculatedWidth: 1180,
            calculatedHeight: 620,
            version: 2,
            wordcloud: JSON.stringify(this.wordsToWordList(model.words)),
        });

        return builder.model;
    }

    buildHeadlineFallback(model) {
        const builder = new SlideModelBuilder("headline2", model);
        builder.createPrimary({
            fullBleed: true,
            showGutter: true,
            headlinePosition: PositionType.CENTER,
            textPosition: PositionType.CENTER,
            text: {
                blocks: [
                    builder.createBlock("Word Cloud", TextStyleType.LABEL),
                    builder.createBlock(model.slideTitle, TextStyleType.HEADING),
                    builder.createBlock(this.buildSummaryText(model), TextStyleType.BODY),
                ].filter(Boolean)
            }
        });

        return builder.model;
    }

    buildListFallback(model) {
        const builder = new SlideModelBuilder("textlist", model);
        builder.createHeader({
            heading: model.slideTitle,
            subHeading: model.summaryLine,
            position: HeaderPositionType.TOP
        });
        builder.createPrimary({
            collectionColor: "theme",
            items: [{
                isMedia: false,
                text: {
                    blocks: model.words.map(word =>
                        builder.createBlock(word, TextStyleType.BULLET_LIST, { indent: 0, bulletColor: null })
                    ).filter(Boolean)
                }
            }]
        });
        if (model.summaryLine) {
            builder.createBottomTray(model.summaryLine);
        }

        return builder.model;
    }

    buildSummaryText(model) {
        const preview = model.words.slice(0, 8).join(", ");
        if (model.summaryLine) {
            return model.summaryLine;
        }
        return preview ? `Primary themes: ${preview}.` : "A visual cluster of weighted keywords.";
    }

    wordsToWordList(words) {
        const breakpoints = this.aiCalcBreakpoints(words.length);
        const wordStrings = this.aiResultsToWordStrings(words, breakpoints);
        const validWeights = [1, 2, 3, 4, 5];
        const wordList = [];

        for (let i = 0; i < validWeights.length; i++) {
            const value = wordStrings[i];
            if (!value?.length) {
                continue;
            }

            const matches = value.match(/"[^"]+"|\b[\w-]+\b/g) || [];
            matches
                .map(item => item.replace(/^"|"$/g, ""))
                .filter(Boolean)
                .forEach(word => {
                    wordList.push({
                        word,
                        weight: `${i + 1}`,
                        layout: {},
                    });
                });
        }

        return wordList.reverse();
    }

    aiCalcBreakpoints(wordCount) {
        if (wordCount === 0) {
            return [0, 0, 0, 0];
        }
        if (wordCount <= 10) {
            return [1, 2, 3, 6];
        }
        if (wordCount <= 20) {
            return [1, 3, 7, 12];
        }
        if (wordCount <= 30) {
            return [1, 4, 9, 18];
        }
        if (wordCount <= 40) {
            return [2, 5, 12, 22];
        }
        if (wordCount <= 50) {
            return [2, 6, 12, 25];
        }
        if (wordCount <= 60) {
            return [2, 6, 13, 25];
        }
        if (wordCount <= 70) {
            return [2, 6, 13, 30];
        }
        return [2, 6, 13, 35];
    }

    aiResultsToWordStrings(words, breakpoints) {
        return [
            this.sliceToString(words, null, breakpoints[0]),
            this.sliceToString(words, breakpoints[0], breakpoints[1]),
            this.sliceToString(words, breakpoints[1], breakpoints[2]),
            this.sliceToString(words, breakpoints[2], breakpoints[3]),
            this.sliceToString(words, breakpoints[3]),
        ].reverse();
    }

    sliceToString(words, start = null, end = null) {
        if (!start) {
            return words.slice(start, end).reverse().join(" ");
        }
        if (start && end) {
            return words.slice(start, end).reverse().join(" ");
        }
        if (!end) {
            return words.slice(start).reverse().join(" ");
        }
        return "";
    }
}
